import React, { useState, useEffect } from "react";
import { ChevronLeft, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetManagerReviewBundleQuery,
  useDecideLeaveAsManagerMutation,
  useForwardLeaveToCfmMutation,
} from "../../slices/leaveApiSlice";
import { useGetDivisionByIdQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function ManagerLeaveRequests() {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const managerId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const managerEmail =
    userInfo?.email || userInfo?.userEmail || userInfo?.managerEmail;
  const managerName = userInfo?.name || userInfo?.userName || "Manager";
  const departmentId = userInfo?.departmentId;

  // RTK Query hooks
  const {
    data: reviewBundleResponse,
    isLoading,
    isError,
    refetch,
  } = useGetManagerReviewBundleQuery(
    { managerId, departmentId },
    { skip: !managerId },
  );

  const [decideLeave] = useDecideLeaveAsManagerMutation();
  const [forwardLeave] = useForwardLeaveToCfmMutation();

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");
  const [selectedApplicantType, setSelectedApplicantType] = useState("All");

  // Options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Forwarded", label: "Forwarded" },
  ];

  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];

  const applicantTypeOptions = [
    { value: "All", label: "All" },
    { value: "Technician", label: "Technician" },
    { value: "Supervisor", label: "Supervisor" },
  ];

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [localLeaveData, setLocalLeaveData] = useState([]);

  // Enhanced note extraction with consistent date handling
  const extractSupervisorInfo = (leave) => {
    // First, check if supervisor info is directly in the leave object
    if (leave.supervisorNote || leave.supervisorEmail) {
      const result = {
        note: leave.supervisorNote || "",
        email: leave.supervisorEmail || "",
        name: leave.supervisorName || "",
        role: "Supervisor",
        date:
          leave.supervisorNoteDate ||
          leave.supervisorUpdatedAt ||
          leave.forwardedAt ||
          leave.updatedAt ||
          leave.createdAt ||
          "",
      };
      return result;
    }

    // Check workflow notes array - EXCLUDE submission notes
    if (leave.notes && Array.isArray(leave.notes)) {
      const supervisorNotes = leave.notes
        .filter(
          (note) =>
            (note.actorRole === "SUPERVISOR" ||
              note.actorRole === "Supervisor") &&
            note.message !== "Submitted leave request",
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp || b.createdAt || b.date) -
            new Date(a.timestamp || a.createdAt || a.date),
        );

      if (supervisorNotes.length > 0) {
        const latestNote = supervisorNotes[0];
        const result = {
          note: latestNote.message || latestNote.note || "",
          email: latestNote.actorEmail || latestNote.actorId || "",
          name: latestNote.actorName || "Supervisor",
          role: latestNote.actorRole || "Supervisor",
          date:
            latestNote.timestamp ||
            latestNote.createdAt ||
            latestNote.date ||
            latestNote.updatedAt ||
            "",
        };
        return result;
      }
    }

    // Check workflow states
    if (leave.workflowStates && Array.isArray(leave.workflowStates)) {
      const supervisorState = leave.workflowStates.find(
        (state) => state.role === "SUPERVISOR" || state.role === "Supervisor",
      );

      if (supervisorState) {
        const result = {
          note: supervisorState.note || "",
          email: supervisorState.actorEmail || "",
          name: supervisorState.actorName || "Supervisor",
          role: supervisorState.role || "Supervisor",
          date:
            supervisorState.timestamp ||
            supervisorState.updatedAt ||
            supervisorState.createdAt ||
            "",
        };
        return result;
      }
    }

    return { note: "", email: "", name: "", role: "Supervisor", date: "" };
  };

  const extractManagerInfo = (leave) => {
    // Check if manager info is directly in the leave object
    if (leave.managerNote || leave.managerEmail) {
      const result = {
        note: leave.managerNote || "",
        email: leave.managerEmail || "",
        name: leave.managerName || "",
        role: "Manager",
        date:
          leave.managerNoteDate ||
          leave.managerUpdatedAt ||
          leave.decisionAt ||
          leave.updatedAt ||
          leave.createdAt ||
          "",
      };
      return result;
    }

    // Check workflow notes array for manager
    if (leave.notes && Array.isArray(leave.notes)) {
      const managerNotes = leave.notes
        .filter(
          (note) =>
            note.actorRole === "MANAGER" || note.actorRole === "Manager",
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp || b.createdAt || b.date) -
            new Date(a.timestamp || a.createdAt || a.date),
        );

      if (managerNotes.length > 0) {
        const latestNote = managerNotes[0];
        const result = {
          note: latestNote.message || latestNote.note || "",
          email: latestNote.actorEmail || latestNote.actorId || "",
          name: latestNote.actorName || "Manager",
          role: latestNote.actorRole || "Manager",
          date:
            latestNote.timestamp ||
            latestNote.createdAt ||
            latestNote.date ||
            latestNote.updatedAt ||
            "",
        };
        return result;
      }
    }

    // Check workflow states for manager
    if (leave.workflowStates && Array.isArray(leave.workflowStates)) {
      const managerState = leave.workflowStates.find(
        (state) => state.role === "MANAGER" || state.role === "Manager",
      );

      if (managerState) {
        const result = {
          note: managerState.note || "",
          email: managerState.actorEmail || "",
          name: managerState.actorName || "Manager",
          role: managerState.role || "Manager",
          date:
            managerState.timestamp ||
            managerState.updatedAt ||
            managerState.createdAt ||
            "",
        };
        return result;
      }
    }

    return { note: "", email: "", name: "", role: "Manager", date: "" };
  };

  const extractCfmInfo = (leave) => {
    // Check if CFM info is directly in the leave object
    if (leave.cfmNote || leave.cfmEmail) {
      const result = {
        note: leave.cfmNote || "",
        email: leave.cfmEmail || "",
        name: leave.cfmName || "",
        role: "CFM",
        date:
          leave.cfmNoteDate ||
          leave.cfmUpdatedAt ||
          leave.decisionAt ||
          leave.updatedAt ||
          leave.createdAt ||
          "",
      };
      return result;
    }

    // Check workflow notes array for CFM
    if (leave.notes && Array.isArray(leave.notes)) {
      const cfmNotes = leave.notes
        .filter(
          (note) =>
            note.actorRole === "CFM" ||
            note.actorRole === "Cfm" ||
            note.actorRole === "cfm",
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp || b.createdAt || b.date) -
            new Date(a.timestamp || a.createdAt || a.date),
        );

      if (cfmNotes.length > 0) {
        const latestNote = cfmNotes[0];
        const result = {
          note: latestNote.message || latestNote.note || "",
          email: latestNote.actorEmail || latestNote.actorId || "",
          name: latestNote.actorName || "CFM",
          role: latestNote.actorRole || "CFM",
          date:
            latestNote.timestamp ||
            latestNote.createdAt ||
            latestNote.date ||
            latestNote.updatedAt ||
            "",
        };
        return result;
      }
    }

    // Check workflow states for CFM
    if (leave.workflowStates && Array.isArray(leave.workflowStates)) {
      const cfmState = leave.workflowStates.find(
        (state) => state.role === "CFM" || state.role === "Cfm",
      );

      if (cfmState) {
        const result = {
          note: cfmState.note || "",
          email: cfmState.actorEmail || "",
          name: cfmState.actorName || "CFM",
          role: cfmState.role || "CFM",
          date:
            cfmState.timestamp ||
            cfmState.updatedAt ||
            cfmState.createdAt ||
            "",
        };
        return result;
      }
    }

    return { note: "", email: "", name: "", role: "CFM", date: "" };
  };

  // NEW: Check if a technician leave is rejected by supervisor
  const isTechnicianRejectedBySupervisor = (leave) => {
    // Only check for technician leaves
    if (leave.applicantRole !== "TECHNICIAN") return false;

    // If there's a supervisor note that indicates rejection
    if (leave.supervisorNote && leave.supervisorNote.trim() !== "") {
      const supervisorNoteLower = leave.supervisorNote.toLowerCase();
      const rejectionKeywords = [
        "reject",
        "deny",
        "not approved",
        "cannot approve",
        "disapproved",
      ];

      // Check if supervisor note contains rejection keywords
      if (
        rejectionKeywords.some((keyword) =>
          supervisorNoteLower.includes(keyword),
        )
      ) {
        return true;
      }
    }

    // Check workflow states for supervisor rejection
    if (leave._rawLeave?.workflowStates) {
      const supervisorState = leave._rawLeave.workflowStates.find(
        (state) => state.role === "SUPERVISOR" || state.role === "Supervisor",
      );

      if (supervisorState?.status === "REJECTED") {
        return true;
      }
    }

    // Check if backend status indicates supervisor rejection
    if (leave._rawLeave?.status === "REJECTED" && !leave.managerNote) {
      return true;
    }

    return false;
  };

  // Transform data without filtering
  const transformLeaveData = (data) => {
    if (!data) return [];

    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.createdAt);
      const dateB = new Date(b.submittedAt || b.createdAt);
      return dateB - dateA;
    });

    return sortedData.map((leave) => {
      const days = calculateLeaveDays(leave);
      const isSingleDay =
        days <= 1 ||
        leave.duration === "FULL_DAY" ||
        leave.duration === "HALF_DAY";

      const leaveTypeValue = leave.leaveType || leave.leaveTypeLabel;
      const isManualLeaveFlag = isManualLeave(leave);
      const leaveTypeDisplay = getLeaveTypeDisplay(leaveTypeValue);
      const status = getStatusFromBackend(leave.status, leaveTypeValue);

      const userEmail =
        leave.Email ||
        (leave.userId &&
        typeof leave.userId === "string" &&
        leave.userId.includes("@")
          ? leave.userId
          : "Unknown User");

      // Extract all note information with consistent date handling
      const supervisorInfo = extractSupervisorInfo(leave);
      const managerInfo = extractManagerInfo(leave);
      const cfmInfo = extractCfmInfo(leave);

      return {
        id: leave.id || leave._id,
        user: userEmail,
        position:
          leave.user?.position ||
          (leave.applicantRole === "SUPERVISOR" ? "Supervisor" : "Technician"),
        type: leaveTypeDisplay,
        reason: leave.reason || "No reason provided",
        days: leave.totalDays,
        startDate: leave.startDate,
        endDate: leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: status,
        originalLeaveType: leave.leaveTypeLabel || leaveTypeValue,
        divisionId: leave.divisionId,
        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
        applicantRole: leave.applicantRole || "TECHNICIAN",
        isManualLeave: isManualLeaveFlag,
        isSingleDay: isSingleDay,

        // Enhanced note information with consistent dates
        supervisorNote: supervisorInfo.note,
        supervisorName: supervisorInfo.name,
        supervisorEmail: supervisorInfo.email,
        supervisorRole: supervisorInfo.role,
        supervisorNoteDate: supervisorInfo.date,

        managerNote: managerInfo.note,
        managerName: managerInfo.name,
        managerEmail: managerInfo.email,
        managerRole: managerInfo.role,
        managerNoteDate: managerInfo.date,

        cfmNote: cfmInfo.note,
        cfmName: cfmInfo.name,
        cfmEmail: cfmInfo.email,
        cfmRole: cfmInfo.role,
        cfmNoteDate: cfmInfo.date,

        _rawLeave: leave,
      };
    });
  };

  // Update local data when API data changes
  useEffect(() => {
    if (reviewBundleResponse) {
      const allLeaves = [
        ...(reviewBundleResponse.technicianLeaves || []),
        ...(reviewBundleResponse.supervisorLeaves || []),
      ];
      const transformedData = transformLeaveData(allLeaves);
      setLocalLeaveData(transformedData);

      // Debug: Log the data to see what we're getting
      console.log("Total leaves:", transformedData.length);
      console.log(
        "All leave statuses:",
        transformedData.map((l) => ({
          user: l.user,
          status: l.status,
          applicantRole: l.applicantRole,
        })),
      );
      console.log(
        "Technician leaves:",
        transformedData.filter((l) => l.applicantRole === "TECHNICIAN").length,
      );
      console.log(
        "Supervisor leaves:",
        transformedData.filter((l) => l.applicantRole === "SUPERVISOR").length,
      );
      console.log(
        "Forwarded leaves:",
        transformedData.filter((l) => l.status === "Forwarded").length,
      );
      const rejectedTechLeaves = transformedData.filter(
        (l) =>
          l.applicantRole === "TECHNICIAN" &&
          isTechnicianRejectedBySupervisor(l),
      );
      console.log(
        "Technician leaves rejected by supervisor:",
        rejectedTechLeaves.length,
      );
      rejectedTechLeaves.forEach((leave) => {
        console.log("Rejected leave:", {
          user: leave.user,
          supervisorNote: leave.supervisorNote,
          status: leave.status,
          managerNote: leave.managerNote,
        });
      });
    }
  }, [reviewBundleResponse]);

  // Division Display Component - Skip query if division doesn't exist to avoid 404s
  const DivisionDisplay = ({ divisionId }) => {
    // For now, just show the division ID as a fallback since many divisions don't exist in DB
    // This avoids unnecessary 404 errors in the console
    if (!divisionId) return <span className="text-gray-600">No division</span>;
    return (
      <span className="text-gray-600">
        Division {divisionId?.substring(0, 8)}
      </span>
    );
  };

  const calculateLeaveDays = (leave) => {
    if (!leave.startDate) return 0;
    switch (leave.duration) {
      case "HALF_DAY":
        return 0.5;
      case "FULL_DAY":
        return 1;
      case "MULTI_DAY":
        if (leave.endDate && leave.startDate !== leave.endDate) {
          const start = new Date(leave.startDate);
          const end = new Date(leave.endDate);
          const diffTime = Math.abs(end - start);
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        return 1;
      default:
        return 1;
    }
  };

  const getLeaveTypeDisplay = (backendLeaveType) => {
    if (!backendLeaveType) return "Manual Leave";
    if (backendLeaveType === "CASUAL") return "Casual Leave";
    else if (backendLeaveType === "EARN") return "Earned Leave";
    else return "Manual Leave";
  };

  const getStatusFromBackend = (backendStatus, leaveType) => {
    if (isManualLeave({ leaveType })) return "Approved";
    const statusMap = {
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      ESCALATED: "Approved",
    };
    return statusMap[backendStatus] || backendStatus || "Pending";
  };

  const isManualLeave = (leave) => {
    const leaveType = leave.leaveType || leave.leaveTypeLabel;
    return !leaveType || (leaveType !== "CASUAL" && leaveType !== "EARN");
  };

  // ✅ FIXED: Check if leave requires manager action
  const requiresManagerAction = (leave) => {
    return (
      leave.applicantRole === "TECHNICIAN" &&
      (leave.status === "Pending" || leave.status === "Forwarded") &&
      !leave.isManualLeave
    );
  };

  const isManagerManualLeave = (leave) => {
    return (
      (leave.applicantRole === "MANAGER" && leave.isManualLeave) ||
      (leave.user === managerEmail &&
        leave.isManualLeave &&
        leave.position?.toLowerCase().includes("manager"))
    );
  };

  const handleBack = () => {
    navigate("/manager/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getApplicantTypeColor = (applicantRole) => {
    switch (applicantRole) {
      case "SUPERVISOR":
        return "bg-purple-100 text-purple-800";
      case "TECHNICIAN":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      date.setHours(date.getHours() + 6);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // FIXED: Enhanced date formatting to handle various date formats consistently
  const formatDateTime = (dateString) => {
    if (!dateString) return "No date available";

    try {
      let date;

      // Handle different date formats
      if (typeof dateString === "string") {
        if (dateString.includes("T")) {
          // ISO format
          date = new Date(dateString);
        } else if (dateString.includes(",")) {
          // Format like "Nov 27, 2025, 02:06 AM"
          date = new Date(dateString.replace(/, /g, " "));
        } else {
          // Fallback
          date = new Date(dateString);
        }
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return "Invalid date";
      }

      // Add 6 hours for timezone adjustment
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Date format error";
    }
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
    setActionNote("");
    setNoteError("");
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
    setActionNote("");
    setNoteError("");
  };

  const validateNote = () => {
    if (!actionNote.trim()) {
      setNoteError("Please provide a note for your action");
      return false;
    }
    setNoteError("");
    return true;
  };

  // ✅ IMMEDIATE SUCCESS: Handle approve/reject for technician leaves
  const handleTechnicianDecision = async (decision) => {
    if (!validateNote() || !selectedLeave) return;

    try {
      // ✅ IMMEDIATELY show success message FIRST
      toast.success(`Leave ${decision.toLowerCase()} successfully!`);

      // ✅ Update local state immediately
      const updatedLeaveData = localLeaveData.map((leave) =>
        leave.id === selectedLeave.id
          ? {
              ...leave,
              status: decision === "APPROVE" ? "Approved" : "Rejected",
              managerNote: actionNote,
              managerName: managerName,
              managerEmail: managerEmail,
              managerNoteDate: new Date().toISOString(),
            }
          : leave,
      );

      setLocalLeaveData(updatedLeaveData);

      // ✅ Close the modal
      handleCloseDetails();

      // ✅ Try API call in background (but don't wait for it)
      try {
        await decideLeave({
          leaveId: selectedLeave.id,
          managerId: managerId,
          decision: decision,
          note: actionNote,
        }).unwrap();

        // Refetch data to sync with server
        refetch();
      } catch (apiError) {
        console.error("API Error:", apiError);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // ✅ IMMEDIATE SUCCESS: Handle reject for supervisor leaves
  const handleSupervisorReject = async () => {
    if (!validateNote() || !selectedLeave) return;

    try {
      // ✅ IMMEDIATELY show success message FIRST
      toast.success("Supervisor leave rejected successfully!");

      // Update local state
      const updatedLeaveData = localLeaveData.map((leave) =>
        leave.id === selectedLeave.id
          ? {
              ...leave,
              status: "Rejected",
              managerNote: actionNote,
              managerName: managerName,
              managerEmail: managerEmail,
              managerNoteDate: new Date().toISOString(),
            }
          : leave,
      );

      setLocalLeaveData(updatedLeaveData);
      handleCloseDetails();

      // Try API call in background
      try {
        await decideLeave({
          leaveId: selectedLeave.id,
          managerId: managerId,
          decision: "REJECT",
          note: actionNote,
        }).unwrap();
        refetch();
      } catch (apiError) {
        console.error("API Error:", apiError);
      }
    } catch (error) {
      console.error("❌ Unexpected error:", error);
    }
  };

  // ✅ IMMEDIATE SUCCESS: Handle forward to CFM
  const handleForwardToCFM = async () => {
    if (!validateNote() || !selectedLeave) return;

    try {
      const cfmInfo = userInfo?.cfmInfo || {};

      // ✅ IMMEDIATELY show success message FIRST
      toast.success("Leave forwarded to CFM successfully!");

      // Update local state
      const updatedLeaveData = localLeaveData.map((leave) =>
        leave.id === selectedLeave.id
          ? {
              ...leave,
              status: "Forwarded",
              managerNote: actionNote,
              managerName: managerName,
              managerEmail: managerEmail,
              managerNoteDate: new Date().toISOString(),
            }
          : leave,
      );

      setLocalLeaveData(updatedLeaveData);
      handleCloseDetails();

      // Try API call in background
      try {
        await forwardLeave({
          leaveId: selectedLeave.id,
          managerId: managerId,
          managerEmail: managerEmail,
          cfmId: cfmInfo.id || cfmInfo._id,
          cfmEmail: cfmInfo.email,
          note: actionNote,
        }).unwrap();
        refetch();
      } catch (apiError) {
        console.error("API Error:", apiError);
      }
    } catch (error) {
      console.error("❌ Unexpected error:", error);
    }
  };

  // Filter data - FIXED VERSION
  const filteredData = localLeaveData
    .filter((record) => {
      // 1. Remove manager's own manual leaves
      if (isManagerManualLeave(record)) return false;

      // 2. Check if technician leave is rejected by supervisor
      if (record.applicantRole === "TECHNICIAN") {
        if (isTechnicianRejectedBySupervisor(record)) {
          return false; // Hide supervisor-rejected technician leaves
        }
      }

      return true;
    })
    .filter((record) => {
      // 3. Apply UI filters (status, leave type, applicant type)
      const statusMatch =
        selectedStatus === "All" || record.status === selectedStatus;
      const leaveTypeMatch =
        selectedLeaveType === "All" || record.type === selectedLeaveType;
      const applicantTypeMatch =
        selectedApplicantType === "All" ||
        (selectedApplicantType === "Technician" &&
          record.applicantRole === "TECHNICIAN") ||
        (selectedApplicantType === "Supervisor" &&
          record.applicantRole === "SUPERVISOR");

      return statusMatch && leaveTypeMatch && applicantTypeMatch;
    });

  // Show loading if manager info is not available
  if (!managerId) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading manager information...</div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading leave requests...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error loading leave requests. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-1 mb-4 sm:mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className=" md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center border-black rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700 sm:w-6 sm:h-6"
          />
        </button>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            Manager Leave Requests
          </h1>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">
                Leave Review Queue
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Review technician and supervisor leave applications
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Status Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Status
              </label>
              <CustomSelect
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                placeholder="All"
                minWidth={160}
              />
            </div>

            {/* Leave Type Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Leave Type
              </label>
              <CustomSelect
                options={leaveTypeOptions}
                value={selectedLeaveType}
                onChange={setSelectedLeaveType}
                placeholder="All"
                minWidth={160}
              />
            </div>

            {/* Applicant Type Filter */}
            <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">
                Applicant Type
              </label>
              <CustomSelect
                options={applicantTypeOptions}
                value={selectedApplicantType}
                onChange={setSelectedApplicantType}
                placeholder="All"
                minWidth={160}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="h-[500px] overflow-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-[#] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  USER
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TYPE
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DIVISION
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  APPLICANT
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DAYS
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  SUBMITTED ON
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  From
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  To
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {filteredData.length > 0 ? (
                filteredData.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(record)}
                  >
                    {/* User & Position */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      <div>
                        <div>{record.user}</div>
                        <div className="text-sm text-zinc-600">
                          {record.position || "No position"}
                        </div>
                      </div>
                    </td>

                    {/* Leave Type */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.type}
                    </td>

                    {/* Division */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.divisionId ? (
                        <DivisionDisplay divisionId={record.divisionId} />
                      ) : (
                        <span className="text-zinc-400 italic">
                          No Division
                        </span>
                      )}
                    </td>

                    {/* Role Badge */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getApplicantTypeColor(record.applicantRole)}`}
                      >
                        {record.applicantRole === "SUPERVISOR"
                          ? "Supervisor"
                          : "Technician"}
                      </span>
                    </td>

                    {/* Duration */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </td>

                    {/* Submitted Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.submittedDate)}
                    </td>

                    {/* Start Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.startDate)}
                    </td>

                    {/* End Date or Single Day */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {record.isSingleDay
                        ? formatDate(record.startDate)
                        : formatDate(record.endDate)}
                    </td>

                    {/* Status Badge */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
                      >
                        {record.status.replace(/_/g, " ")}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    No leave requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enhanced Leave Details Overlay with Consistent Note Dates */}
      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-3 sm:p-4">
              {/* Header with Status and Profile */}
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <span
                      className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}
                    >
                      {selectedLeave.status}
                    </span>
                    <span
                      className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getApplicantTypeColor(selectedLeave.applicantRole)}`}
                    >
                      {selectedLeave.applicantRole === "SUPERVISOR"
                        ? "Supervisor"
                        : "Technician"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.user.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                        {selectedLeave.user}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {selectedLeave.position}
                      </p>
                      {selectedLeave.isManualLeave && (
                        <p className="text-xs text-blue-600 mt-1">
                          Added by Manager
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              {/* Division Information */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Division:
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base font-medium">
                    {selectedLeave.divisionId ? (
                      <DivisionDisplay divisionId={selectedLeave.divisionId} />
                    ) : (
                      "No Division"
                    )}
                  </span>
                </div>
              </div>

              {/* Leave Type */}
              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
                  {selectedLeave.originalLeaveType}
                </h3>
              </div>

              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">
                  Reason:
                </h4>
                <div className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <div className="text-sm text-blue-600 mt-1">
                      Half Day -{" "}
                      {selectedLeave.halfDaySession === "MORNING"
                        ? "Morning"
                        : "Afternoon"}{" "}
                      Session
                    </div>
                  )}
                  {selectedLeave.reason}
                </div>
              </div>

              {/* Dates and Days */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Submitted On:
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>
                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                      Leave Date:
                    </span>
                    <span className="text-gray-900 text-sm sm:text-base">
                      {formatDate(selectedLeave.startDate)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                        From:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.startDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">
                        To:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Days
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days}{" "}
                    {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Enhanced Notes Section with Consistent Dates */}
              <div className="rounded-lg p-3 sm:p-4">
                {selectedLeave.isManualLeave ? (
                  <div className="text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 text-sm sm:text-base mb-2">
                        Manual Leave Approved
                      </h4>
                      <p className="text-green-700 text-sm">
                        Manual leaves are automatically approved and do not
                        require review.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Supervisor Note */}
                    {selectedLeave.supervisorNote &&
                      selectedLeave.supervisorNote.trim() !== "" && (
                        <div className="mb-4 last:mb-0">
                          <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                                {selectedLeave.supervisorEmail
                                  ?.charAt(0)
                                  ?.toUpperCase() || "S"}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                {selectedLeave.supervisorEmail ||
                                  selectedLeave.supervisorName ||
                                  "Supervisor"}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600">
                                Supervisor
                              </p>
                            </div>
                          </div>
                          <div className="p-1">
                            <p className="text-gray-700 text-sm sm:text-base mb-2">
                              {selectedLeave.supervisorNote}
                            </p>
                            {selectedLeave.supervisorNoteDate && (
                              <p className="text-xs text-gray-500">
                                Note added on:{" "}
                                {formatDateTime(
                                  selectedLeave.supervisorNoteDate,
                                )}
                              </p>
                            )}
                          </div>
                          {(selectedLeave.managerNote ||
                            selectedLeave.cfmNote) && (
                            <div className="border-b border-gray-300 my-4"></div>
                          )}
                        </div>
                      )}

                    {/* Manager Note */}
                    {selectedLeave.managerNote &&
                      selectedLeave.managerNote.trim() !== "" && (
                        <div className="mb-4 last:mb-0">
                          <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-green-600 font-semibold text-xs sm:text-sm">
                                {selectedLeave.managerEmail
                                  ?.charAt(0)
                                  ?.toUpperCase() || "M"}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                {selectedLeave.managerEmail ||
                                  selectedLeave.managerName ||
                                  "Manager"}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600">
                                Manager
                              </p>
                            </div>
                          </div>
                          <div className="p-1">
                            <p className="text-gray-700 text-sm sm:text-base mb-2">
                              {selectedLeave.managerNote}
                            </p>
                            {selectedLeave.managerNoteDate && (
                              <p className="text-xs text-gray-500">
                                Note added on:{" "}
                                {formatDateTime(selectedLeave.managerNoteDate)}
                              </p>
                            )}
                          </div>
                          {selectedLeave.cfmNote && (
                            <div className="border-b border-gray-300 my-4"></div>
                          )}
                        </div>
                      )}

                    {/* CFM Note */}
                    {selectedLeave.cfmNote &&
                      selectedLeave.cfmNote.trim() !== "" && (
                        <div className="mb-4 last:mb-0">
                          <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-purple-600 font-semibold text-xs sm:text-sm">
                                {selectedLeave.cfmEmail
                                  ?.charAt(0)
                                  ?.toUpperCase() || "C"}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                {selectedLeave.cfmEmail ||
                                  selectedLeave.cfmName ||
                                  "CFM"}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600">
                                CFM
                              </p>
                            </div>
                          </div>
                          <div className="p-1">
                            <p className="text-gray-700 text-sm sm:text-base mb-2">
                              {selectedLeave.cfmNote}
                            </p>
                            {selectedLeave.cfmNoteDate && (
                              <p className="text-xs text-gray-500">
                                Note added on:{" "}
                                {formatDateTime(selectedLeave.cfmNoteDate)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>

              {/* Action Sections */}
              {/* For Technician leaves with Pending or Forwarded status */}
              {requiresManagerAction(selectedLeave) && (
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note for your decision
                    </label>
                    <textarea
                      value={actionNote}
                      onChange={(e) => {
                        setActionNote(e.target.value);
                        if (noteError && e.target.value.trim())
                          setNoteError("");
                      }}
                      rows={3}
                      placeholder="Enter your note here..."
                      className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${noteError ? "border-red-500" : "border-gray-300"}`}
                    />
                    {noteError && (
                      <p className="text-red-500 text-[#ABABAB] mt-1 flex items-center">
                        <span className="mr-1">⚠</span>
                        {noteError}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={() => handleTechnicianDecision("REJECT")}
                      className="px-8 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm sm:text-base whitespace-nowrap"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleTechnicianDecision("APPROVE")}
                      className="px-8 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm sm:text-base whitespace-nowrap"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              )}

              {/* Action Section for Supervisor applications */}
              {selectedLeave.applicantRole === "SUPERVISOR" &&
                selectedLeave.status === "Pending" &&
                !selectedLeave.isManualLeave && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note for your decision
                      </label>
                      <textarea
                        value={actionNote}
                        onChange={(e) => {
                          setActionNote(e.target.value);
                          if (noteError && e.target.value.trim()) {
                            setNoteError("");
                          }
                        }}
                        rows={3}
                        placeholder="Enter your note here..."
                        className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${
                          noteError ? "border-red-500" : "border-gray-300"
                        }`}
                      />
                      {noteError && (
                        <p className="text-red-500 text-[#ABABAB] mt-1 flex items-center">
                          <span className="mr-1">⚠</span>
                          {noteError}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons for Supervisor applications */}
                    <div className="flex space-x-3 justify-center">
                      <button
                        onClick={handleSupervisorReject}
                        className="px-8 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm sm:text-base whitespace-nowrap"
                      >
                        Reject
                      </button>
                      <button
                        onClick={handleForwardToCFM}
                        className="px-8 bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm sm:text-base whitespace-nowrap"
                      >
                        Forward to CFM
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
